import { useIntl } from 'react-intl';
import { Link, useLocation, useParams } from 'react-router-dom';

import { accessKeyMessages } from 'src/features/accessKey/utils/messages';
import { administrationMessages } from 'src/features/administration/utils/messages';
import { PERMISSION } from 'src/features/auth/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useMediaQuery } from 'src/hooks/useMediaQuery';
import { SectionBlock, SectionBlockSeparator, SectionBlockTitle } from 'src/registry/components/blocks/SectionBlock';
import { useSidePanel } from 'src/registry/providers/SidePanelProvider';
import { Button } from 'src/registry/ui/Button';
import { cn } from 'src/utils/shadcn';

const SidePanelButton = ({
    urlLink,
    title,
    additionalActivePaths = [],
}: {
    urlLink: string;
    title: string;
    additionalActivePaths?: string[];
}) => {
    const { pathname } = useLocation();
    const { closePanel } = useSidePanel();

    return (
        <Button
            asChild
            className="h-8 justify-start px-1 py-4"
            variant={[urlLink, ...additionalActivePaths].includes(pathname) ? 'activeGray' : 'link'}
            onClick={() => closePanel('parking-management-side-panel')}
        >
            <Link to={urlLink} style={{ textDecoration: 'none' }}>
                {title}
            </Link>
        </Button>
    );
};

function AdministrationSidebar() {
    const {
        siteId = '',
        zoneId = '',
        gatewayId = '',
        planId = '',
        identity = '',
        membershipTypeId = '',
        accountId = '',
        validationTypeId = '',
        roleId = '',
        listId = '',
    } = useParams();
    const isMobile = useMediaQuery('(max-width: 639px)');
    const { formatMessage } = useIntl();

    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const accessServiceProviders = useHasPermissions([PERMISSION.ACCESS_SERVICE_PROVIDERS]);
    const accessZones = useHasPermissions([PERMISSION.ACCESS_ZONES]);
    const accessGateways = useHasPermissions([PERMISSION.ACCESS_GATEWAYS]);
    const accessSpaces = useHasPermissions([PERMISSION.ACCESS_SPACES]);
    const accessRoles = useHasPermissions([PERMISSION.MANAGE_ROLES]);
    const accessUsers = useHasPermissions([PERMISSION.ACCESS_USERS]);
    const manageSystemSettings = useHasPermissions([PERMISSION.MANAGE_SYSTEM_SETTINGS]);
    const manageSystemComponents = useHasPermissions([PERMISSION.MANAGE_SYSTEM_COMPONENTS]);
    const manageApiKeys = useHasPermissions([PERMISSION.MANAGE_API_KEYS]);
    const accessAccessKeys = useHasPermissions([PERMISSION.ACCESS_ACCESS_KEYS]);
    const accessMembershipTypes = useHasPermissions([PERMISSION.ACCESS_MEMBERSHIP_TYPES]);
    const accessTroubleshooting = useHasPermissions([PERMISSION.ACCESS_TROUBLESHOOTING]);
    const accessPricingPlans = useHasPermissions([PERMISSION.ACCESS_PRICING_PLANS]);
    const accessValidatorAccounts = useHasPermissions([PERMISSION.MANAGE_VALIDATIONS]);
    const accessAllowLists = useHasPermissions([PERMISSION.ACCESS_ALLOW_LISTS]);

    const showUsersSectionTitle = accessUsers || accessRoles;
    const showSitesSectionTitle =
        accessServiceProviders || accessSites || accessZones || accessGateways || accessSpaces;

    return (
        <div className={cn({ 'sticky z-40 dark:bg-gray-900': !isMobile })}>
            <SectionBlock className="mt-4 flex flex-col sm:gap-3">
                {showUsersSectionTitle && (
                    <SectionBlockTitle className="text-base">
                        {formatMessage(administrationMessages.users)}
                    </SectionBlockTitle>
                )}
                {accessUsers && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/users`}
                        title={formatMessage(administrationMessages.users)}
                    />
                )}
                {accessRoles && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/roles`}
                        title={formatMessage(administrationMessages.roles)}
                        additionalActivePaths={[`/${PATH.ADMINISTRATION}/roles/${roleId}`]}
                    />
                )}
                {showSitesSectionTitle && (
                    <>
                        {showUsersSectionTitle && <SectionBlockSeparator className="my-2" />}
                        <SectionBlockTitle className="text-base">
                            {formatMessage(administrationMessages.sites)}
                        </SectionBlockTitle>
                    </>
                )}

                {accessServiceProviders && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/service-provider`}
                        title={formatMessage(administrationMessages.organisations)}
                    />
                )}
                {accessSites && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/site`}
                        title={formatMessage(administrationMessages.sites)}
                        additionalActivePaths={[`/${PATH.ADMINISTRATION}/site/${siteId}`]}
                    />
                )}
                {accessZones && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/zone`}
                        title={formatMessage(administrationMessages.zones)}
                        additionalActivePaths={[`/${PATH.ADMINISTRATION}/zone/${zoneId}`]}
                    />
                )}
                {accessGateways && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/gateway`}
                        title={formatMessage(administrationMessages.gateways)}
                        additionalActivePaths={[`/${PATH.ADMINISTRATION}/gateway/${gatewayId}`]}
                    />
                )}
                {accessSpaces && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/spaces`}
                        title={formatMessage(administrationMessages.spaces)}
                    />
                )}

                {accessAllowLists && (
                    <>
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/white-list`}
                            title={formatMessage(administrationMessages.whiteLists)}
                            additionalActivePaths={[`/${PATH.ADMINISTRATION}/white-list/${listId}`]}
                        />
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/black-list`}
                            title={formatMessage(administrationMessages.blackLists)}
                            additionalActivePaths={[`/${PATH.ADMINISTRATION}/black-list/${listId}`]}
                        />
                    </>
                )}

                {accessMembershipTypes && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(membershipTypeMessages.memberships)}
                        </SectionBlockTitle>
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/membership-templates`}
                            title={formatMessage(membershipTypeMessages.templates)}
                            additionalActivePaths={[`/${PATH.ADMINISTRATION}/membership-templates/${membershipTypeId}`]}
                        />
                    </>
                )}

                {accessAccessKeys && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(accessKeyMessages.accessKeys)}
                        </SectionBlockTitle>
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/access-key`}
                            title={formatMessage(accessKeyMessages.accessKeys)}
                        />
                    </>
                )}

                {accessPricingPlans && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(administrationMessages.prices)}
                        </SectionBlockTitle>
                        {accessPricingPlans && (
                            <SidePanelButton
                                urlLink={`/${PATH.ADMINISTRATION}/pricing-plan`}
                                title={formatMessage(administrationMessages.pricingPlans)}
                                additionalActivePaths={[
                                    `/${PATH.ADMINISTRATION}/pricing-plan/${planId}`,
                                    `/${PATH.ADMINISTRATION}/pricing-plan/${planId}/${identity}`,
                                ]}
                            />
                        )}
                    </>
                )}

                {accessValidatorAccounts && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(administrationMessages.validations)}
                        </SectionBlockTitle>
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/validator-accounts`}
                            title={formatMessage(administrationMessages.validatorAccounts)}
                            additionalActivePaths={[`/${PATH.ADMINISTRATION}/validator-accounts/${accountId}`]}
                        />
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/validation-types`}
                            title={formatMessage(administrationMessages.validationTypes)}
                            additionalActivePaths={[`/${PATH.ADMINISTRATION}/validation-types/${validationTypeId}`]}
                        />
                    </>
                )}
                {/*{accessServiceProviders && (*/}
                {/*    <>*/}
                {/*        <SectionBlockSeparator className="my-2" />*/}
                {/*        <SectionBlockTitle className="text-base">*/}
                {/*            {formatMessage(administrationMessages.billingSettings)}*/}
                {/*        </SectionBlockTitle>*/}
                {/*        <SidePanelButton*/}
                {/*            urlLink={`/${PATH.ADMINISTRATION}/billing-entity`}*/}
                {/*            title={formatMessage(billingMessages.billingEntities)}*/}
                {/*        />*/}
                {/*    </>*/}
                {/*)}*/}
                {accessTroubleshooting && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(administrationMessages.troubleshooting)}
                        </SectionBlockTitle>
                        <SidePanelButton
                            urlLink={`/${PATH.ADMINISTRATION}/actions`}
                            title={formatMessage(administrationMessages.actions)}
                        />
                    </>
                )}
                {(manageSystemComponents || manageSystemSettings) && (
                    <>
                        <SectionBlockSeparator className="my-2" />
                        <SectionBlockTitle className="text-base">
                            {formatMessage(administrationMessages.systems)}
                        </SectionBlockTitle>
                    </>
                )}
                {manageSystemSettings && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/system-settings`}
                        title={formatMessage(administrationMessages.settings)}
                    />
                )}
                {manageSystemComponents && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/system-components`}
                        title={formatMessage(administrationMessages.components)}
                    />
                )}
                {manageApiKeys && (
                    <SidePanelButton
                        urlLink={`/${PATH.ADMINISTRATION}/api-keys`}
                        title={formatMessage(administrationMessages.apiKeys)}
                    />
                )}
            </SectionBlock>
        </div>
    );
}

export default AdministrationSidebar;
